import { AcessoCbo, SessaoQuery, TipoEstabelecimentoEnum, TipoUnidadeSaudeEnum } from 'graphql/types.generated'
import { ExclusiveUnion } from 'types/ExclusiveUnion'
import { Permission } from 'types/Permissions'

export type AcessoProfissionalType = SessaoQuery['sessao']['acesso']

export interface CboAndPermissionAuthorization {
  hasAnyCbo: AcessoCbo[][]
  hasAnyPermission: Permission[]
}

export interface PermissionAuthorization {
  permission: Authorization
}

export interface PreAuthorizedAuthorization {
  preAuthorized: true
}

export interface TipoEstabelecimentoAuthorization {
  tiposEstabelecimento?: TipoEstabelecimentoEnum[]
}

export type BaseAuthorization = ExclusiveUnion<
  PermissionAuthorization | CboAndPermissionAuthorization | PreAuthorizedAuthorization
>

export type AllAuthorizations = BaseAuthorization & TipoEstabelecimentoAuthorization

export type Authorization = Permission | Permission[] | AcessoCbo[] | TipoUnidadeSaudeEnum[]

export const isCboAndPermissionAuthorization = (
  x: AllAuthorizations
): x is AllAuthorizations & CboAndPermissionAuthorization =>
  (x as CboAndPermissionAuthorization).hasAnyCbo !== undefined

export const isPermissionAuthorization = (x: AllAuthorizations): x is AllAuthorizations & PermissionAuthorization =>
  (x as PermissionAuthorization).permission !== undefined

export const isPreAuthorizedAuthorization = (
  x: AllAuthorizations
): x is AllAuthorizations & PreAuthorizedAuthorization => (x as PreAuthorizedAuthorization).preAuthorized === true

export const getAuthorizationProps = (item: AllAuthorizations): AllAuthorizations => {
  const baseProps: TipoEstabelecimentoAuthorization = {
    tiposEstabelecimento: item.tiposEstabelecimento,
  }

  if (isPreAuthorizedAuthorization(item)) {
    return {
      ...baseProps,
      preAuthorized: true,
    }
  }

  if (isCboAndPermissionAuthorization(item)) {
    return {
      ...baseProps,
      hasAnyCbo: item.hasAnyCbo,
      hasAnyPermission: item.hasAnyPermission,
    }
  }

  return {
    ...baseProps,
    permission: item.permission,
  }
}

export const unidadeSaudeTipoCoMsRecord: Record<TipoUnidadeSaudeEnum, number> = {
  OUTROS: -1,
  POSTO_DE_SAUDE: 1,
  CENTRO_DE_SAUDE_UNIDADE_BASICA: 2,
  POLICLINICA: 4,
  HOSPITAL_GERAL: 5,
  HOSPITAL_ESPECIALIZADO: 7,
  UNIDADE_MISTA: 15,
  PRONTO_SOCORRO_GERAL: 20,
  PRONTO_SOCORRO_ESPECIALIZADO: 21,
  CONSULTORIO_ISOLADO: 22,
  UNIDADE_MOVEL_FLUVIAL: 32,
  CLINICA_CENTRO_DE_ESPECIALIDADE: 36,
  UNIDADE_DE_APOIO_DIAGNOSE_E_TERAPIA_SADT_ISOLADO: 39,
  UNIDADE_MOVEL_TERRESTRE: 40,
  UNIDADE_MOVEL_DE_NIVEL_PRE_HOSPITALAR_NA_AREA_DE_URGENCIA: 42,
  FARMACIA: 43,
  UNIDADE_DE_VIGILANCIA_EM_SAUDE: 50,
  COOPERATIVA_OU_EMPRESA_DE_CESSAO_DE_TRABALHADORES_NA_SAUDE: 60,
  CENTRO_DE_PARTO_NORMAL_ISOLADO: 61,
  HOSPITAL_DIA_ISOLADO: 62,
  CENTRAL_DE_REGULACAO_DE_SERVICOS_DE_SAUDE: 64,
  LABORATORIO_CENTRAL_DE_SAUDE_PUBLICA_LACEN: 67,
  CENTRAL_DE_GESTAO_EM_SAUDE: 68,
  CENTRO_DE_ATENCAO_HEMOTERAPIA_E_OU_HEMATOLOGICA: 69,
  CENTRO_DE_ATENCAO_PSICOSSOCIAL: 70,
  CENTRO_DE_APOIO_A_SAUDE_DA_FAMILIA: 71,
  UNIDADE_DE_ATENCAO_A_SAUDE_INDIGENA: 72,
  PRONTO_ATENDIMENTO: 73,
  POLO_ACADEMIA_DA_SAUDE: 74,
  TELESSAUDE: 75,
  CENTRAL_DE_REGULACAO_MEDICA_DAS_URGENCIAS: 76,
  SERVICO_DE_ATENCAO_DOMICILIAR_ISOLADO_HOME_CARE: 77,
  UNIDADE_DE_ATENCAO_EM_REGIME_RESIDENCIAL: 78,
  OFICINA_ORTOPEDICA: 79,
  LABORATORIO_DE_SAUDE_PUBLICA: 80,
  CENTRAL_DE_REGULACAO_DO_ACESSO: 81,
  CENTRAL_DE_NOTIFICACAO_CAPTACAO_E_DISTRIB_DE_ORGAOS_ESTADUAL: 82,
  POLO_DE_PREVENCAO_DE_DOENCAS_E_AGRAVOS_E_PROMOCAO_DA_SAUDE: 83,
  CENTRAL_DE_ABASTECIMENTO: 84,
  CENTRO_DE_IMUNIZACAO: 85,
}

export const hasPermissionByTipoEstabelecimento = (
  props: AllAuthorizations
): props is AllAuthorizations & { tiposEstabelecimento: TipoEstabelecimentoEnum[] } => !!props.tiposEstabelecimento
